import { createContext, useContext, useLayoutEffect, useRef, type ReactNode } from 'react';
import type { SessionState } from '../session/Session';
import type { Observation } from '../protocol/ui';
import type { Presentation, PresentationV2 } from '../presentation/types';

export function visibleGameContext(state: SessionState, observation: Observation | null = state.observation, revision = state.revision, presentation: Presentation | PresentationV2 | null = null) {
  // Deliberately select fields; never serialize a Session, credential, storage or URL.
  return { gameId: state.gameId, matchId: state.matchId, title: state.title, seat: state.seat,
    revision, liveRevision: state.revision, observation, description: state.description, presentation: state.presentation,
    loadedPresentation: presentation ? { id: presentation.manifest.id, version: presentation.manifest.version,
      artHash: 'artHash' in presentation ? presentation.artHash ?? null : null } : null,
    connection: state.connection, paused: state.paused };
}
type GameView = ReturnType<typeof visibleGameContext>;
const Context = createContext<{ current: GameView | null } | null>(null);
export function IssueContextProvider({ children }: { children: ReactNode }) {
  const value = useRef<GameView | null>(null);
  return <Context.Provider value={value}>{children}</Context.Provider>;
}
export const useIssueContext = () => useContext(Context);
export function useReportGameView(state: SessionState, observation: Observation | null = state.observation, revision = state.revision, presentation: Presentation | PresentationV2 | null = null) {
  const target = useIssueContext();
  useLayoutEffect(() => {
    if (!target) return;
    const value = visibleGameContext(state, observation, revision, presentation);
    target.current = value;
    return () => { if (target.current === value) target.current = null; };
  }, [target, state, observation, revision, presentation]);
}
